import { useMemo } from 'react';

import { useGrowthBook } from '@growthbook/growthbook-react';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';

import { getPricingConfig } from '../../../global/pricingConfig';
import {
  sharedConfigStoreSelector,
  useSharedConfigStore,
} from '../../../stores/sharedConfigStore';
import { userStoreSelector, useUserStore } from '../../../stores/userStore';
import { PricingPlan, PricingPlanAddon } from '../../../types/Pricing';
/**
 * Custom hook to get pricing data for the free, pro and expert plans.
 *
 * It retrieves the necessary data from various stores, including user information, credit limits, team status, country, and subscription prices to determine the pricing plans data.
 */
const usePricingPlansData = ({
  addon,
}: {
  addon: PricingPlanAddon | null;
}): Record<SubscriptionPlanType, Omit<PricingPlan, 'price'>> => {
  const user = useUserStore(userStoreSelector.user);
  const creditLimits = useSharedConfigStore(
    sharedConfigStoreSelector.creditLimits
  );

  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );
  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );

  const growthBook = useGrowthBook();

  return useMemo(() => {
    const planData = getPricingConfig({
      creditLimits,
      designLimits: user?.designsLimitConfig,
      growthBook,
      selectedExpertAddon: !!addon,
      subscriptionPrices,
      country,
    });

    return {
      [SubscriptionPlanType.FREE]: { ...planData.FREE, price: undefined },
      [SubscriptionPlanType.PRO]: { ...planData.PRO, price: undefined },
      [SubscriptionPlanType.EXPERT]: { ...planData.EXPERT, price: undefined },
    };
  }, [
    creditLimits,
    user?.designsLimitConfig,
    growthBook,
    addon,
    subscriptionPrices,
    country,
  ]);
};

export default usePricingPlansData;
